/**
 * Pure utility functions for building CRM events when creating a new object type.
 * Extracted from CreateObjectTypeWizard to keep the component file manageable.
 */

import { createEventBuilder } from '@/lib/crm/core/EventBuilder';
import type { OntologyKind } from '@/lib/crm/core/ontologyKinds';
import { PIPELINE_ONTOLOGY_KIND } from '@/lib/crm/core/ontologyKinds';
import type { BaseEvent } from '@/lib/crm/core/types';

import type { AssociationDefinition } from '../InlineAssociationEditor';
import type { PropertyDefinition } from '../InlinePropertyEditor';
import type { StageItem } from './StageManager';

/** A labeled group of events — kept for readability. */
export interface BatchStep {
  label: string;
  events: BaseEvent[];
}

export interface ObjectTypeFormState {
  entityTypeName: string;
  displayName: string;
  pluralName: string;
  icon: string;
  /**
   * What the object IS — stamped alongside `default` into `ahoy_entity_kind`.
   * Required: a type with no ontology kind is invisible to the Snowflake
   * export and to every kind-based association endpoint (see
   * `lib/crm/core/ontologyKinds.ts`).
   */
  ontologyKind: OntologyKind;
  isPipelineable: boolean;
  pipelineName: string;
  stages: StageItem[];
  properties: PropertyDefinition[];
  associations: AssociationDefinition[];
  notesEnabled: boolean;
}

/** Build property creation events for the given entity type. */
export function buildPropertyStep(
  sourceId: string,
  entityTypeName: string,
  props: PropertyDefinition[]
): BatchStep | null {
  if (props.length === 0) {
    return null;
  }
  const builder = createEventBuilder({ sourceId });
  const usedNames = new Set<string>();
  for (const prop of props) {
    if (!prop.name) {
      throw new Error(
        `Property "${prop.label}" has an invalid generated name. Please use letters or numbers in the label.`
      );
    }
    let propName = prop.name;
    let suffix = 2;
    const baseName = propName;
    while (usedNames.has(propName)) {
      propName = `${baseName}_${suffix}`;
      suffix++;
    }
    usedNames.add(propName);

    const properties: Record<string, unknown> = {
      ahoy_type_name: propName,
      ahoy_entity_type_name: entityTypeName,
      ahoy_field_type_name: prop.fieldType,
      ahoy_label: prop.label.trim(),
      ahoy_description: prop.description.trim() || null,
      ahoy_is_unique_value: prop.isUnique ? 'true' : 'false',
    };
    if (prop.fieldType === 'select' && prop.options.length > 0) {
      properties.ahoy_options = prop.options;
    }
    builder.objectCreated(
      crypto.randomUUID(),
      'ahoy_property_type',
      properties
    );
  }
  return { label: 'Creating properties', events: builder.build() };
}

/** Build association type creation events for the given entity type. */
export function buildAssociationStep(
  sourceId: string,
  entityTypeName: string,
  assocs: AssociationDefinition[]
): BatchStep | null {
  if (assocs.length === 0) {
    return null;
  }
  const builder = createEventBuilder({ sourceId });
  for (const assoc of assocs) {
    builder.objectCreated(crypto.randomUUID(), 'ahoy_association_type', {
      ahoy_type_name: `${entityTypeName}_to_${assoc.targetEntityTypeName}`,
      ahoy_display_name: assoc.displayName.trim(),
      ahoy_display_name_reverse: assoc.displayNameReverse.trim(),
      ahoy_source_entity_type_name: entityTypeName,
      ahoy_target_entity_type_name: assoc.targetEntityTypeName,
      ahoy_cardinality: assoc.cardinality,
    });
  }
  return { label: 'Creating associations', events: builder.build() };
}

/**
 * Build note association events: creates {entityTypeName}_to_ahoy_note
 * association type. The backend's ReverseAssociationCascade automatically
 * creates the reverse (ahoy_note → entityType), which the Notes tab
 * detects via hasNoteAssociation.
 */
export function buildNotesStep(
  sourceId: string,
  entityTypeName: string,
  notesEnabled: boolean,
  pluralName: string,
  displayName: string
): BatchStep | null {
  if (!notesEnabled) {
    return null;
  }
  const builder = createEventBuilder({ sourceId });
  builder.objectCreated(crypto.randomUUID(), 'ahoy_association_type', {
    ahoy_type_name: `${entityTypeName}_to_ahoy_note`,
    ahoy_display_name: 'Notes',
    ahoy_display_name_reverse: pluralName.trim() || displayName.trim(),
    ahoy_source_entity_type_name: entityTypeName,
    ahoy_target_entity_type_name: 'ahoy_note',
    ahoy_cardinality: 'many_to_many',
  });
  return { label: 'Enabling notes', events: builder.build() };
}

/**
 * Build all event batches for submission.
 * The backend processes all batches within a single DB transaction —
 * if any batch fails, everything is rolled back.
 */
export function buildObjectTypeEvents(
  sourceId: string,
  state: ObjectTypeFormState
): BatchStep[] {
  const {
    entityTypeName,
    displayName,
    pluralName,
    icon,
    ontologyKind,
    isPipelineable,
    pipelineName,
    stages,
    properties,
    associations,
    notesEnabled,
  } = state;

  if (!isPipelineable) {
    const builder = createEventBuilder({ sourceId });
    builder.objectCreated(crypto.randomUUID(), 'ahoy_entity_type', {
      ahoy_type_name: entityTypeName,
      ahoy_display_name: displayName.trim(),
      ahoy_display_name_plural: pluralName.trim(),
      ahoy_icon: icon,
      // `default` supplies the shared base properties; the ontology kind is
      // what the rest of the platform reasons over. Never stamp `['default']`
      // alone — see lib/crm/core/ontologyKinds.ts.
      ahoy_entity_kind: ['default', ontologyKind],
    });

    const steps: BatchStep[] = [
      { label: 'Creating object type', events: builder.build() },
    ];
    const propStep = buildPropertyStep(sourceId, entityTypeName, properties);
    if (propStep) {
      steps.push(propStep);
    }
    const assocStep = buildAssociationStep(
      sourceId,
      entityTypeName,
      associations
    );
    if (assocStep) {
      steps.push(assocStep);
    }
    const notesStep = buildNotesStep(
      sourceId,
      entityTypeName,
      notesEnabled,
      pluralName,
      displayName
    );
    if (notesStep) {
      steps.push(notesStep);
    }
    return steps;
  }

  const pipelineTypeName = `${entityTypeName}_pipeline`;
  const stageTypeName = `${entityTypeName}_pipeline_stage`;
  const steps: BatchStep[] = [];

  // Batch 1: Create entity types
  const step1Builder = createEventBuilder({ sourceId });
  step1Builder.objectCreated(crypto.randomUUID(), 'ahoy_entity_type', {
    ahoy_type_name: entityTypeName,
    ahoy_display_name: displayName.trim(),
    ahoy_display_name_plural: pluralName.trim(),
    ahoy_icon: icon,
    // A pipelineable type always rides stages, which IS the `case` shape — the
    // wizard only offers the Pipeline step when `case` was chosen, so this and
    // `ontologyKind` agree by construction (see `kindRidesPipeline`).
    ahoy_entity_kind: ['default', PIPELINE_ONTOLOGY_KIND],
  });
  step1Builder.objectCreated(crypto.randomUUID(), 'ahoy_entity_type', {
    ahoy_type_name: pipelineTypeName,
    ahoy_display_name: `${displayName.trim()} Pipeline`,
    ahoy_display_name_plural: `${displayName.trim()} Pipelines`,
    ahoy_icon: icon,
    ahoy_entity_kind: ['pipeline'],
  });
  step1Builder.objectCreated(crypto.randomUUID(), 'ahoy_entity_type', {
    ahoy_type_name: stageTypeName,
    ahoy_display_name: `${displayName.trim()} Pipeline Stage`,
    ahoy_display_name_plural: `${displayName.trim()} Pipeline Stages`,
    ahoy_icon: icon,
    ahoy_entity_kind: ['stage'],
  });
  steps.push({ label: 'Creating object types', events: step1Builder.build() });

  // Batch 2: Create count property type on stage entity type
  const step2Builder = createEventBuilder({ sourceId });
  step2Builder.objectCreated(crypto.randomUUID(), 'ahoy_property_type', {
    ahoy_type_name: `${entityTypeName}_count`,
    ahoy_entity_type_name: stageTypeName,
    ahoy_field_type_name: 'number',
    ahoy_label: `${displayName.trim()} Count`,
    ahoy_description: `Count of ${pluralName.trim().toLowerCase()} in this stage`,
    ahoy_value_write_access: 'internal',
    ahoy_value_read_access: 'all',
  });
  steps.push({
    label: 'Creating count property',
    events: step2Builder.build(),
  });

  // Batch 3: Create pipeline association types.
  //
  // Each forward direction is paired with an explicit reverse via
  // ahoy_reverse_type_name. This mirrors the system YAML pattern (see
  // association_types.yaml for ahoy_deal_pipeline_to_stage and friends) and
  // prevents the system from auto-generating __reverse aliases that would
  // collide with the explicit forward types' (source, target) and break
  // pipeline_service.PipelineConfig.from_associations name resolution.
  const entityToStage = `${entityTypeName}_to_${stageTypeName}`;
  const stageToEntity = `${stageTypeName}_to_${entityTypeName}`;
  const pipelineToStage = `${pipelineTypeName}_to_${stageTypeName}`;
  const stageToPipeline = `${stageTypeName}_to_${pipelineTypeName}`;
  const entityToPipeline = `${entityTypeName}_to_${pipelineTypeName}`;
  const pipelineToEntity = `${pipelineTypeName}_to_${entityTypeName}`;

  const step3Builder = createEventBuilder({ sourceId });
  step3Builder.objectCreated(crypto.randomUUID(), 'ahoy_association_type', {
    ahoy_type_name: entityToStage,
    ahoy_display_name: 'Pipeline Stage',
    ahoy_display_name_reverse: `${pluralName.trim()} in Stage`,
    ahoy_source_entity_type_name: entityTypeName,
    ahoy_target_entity_type_name: stageTypeName,
    ahoy_cardinality: 'many_to_one',
    ahoy_reverse_type_name: stageToEntity,
  });
  step3Builder.objectCreated(crypto.randomUUID(), 'ahoy_association_type', {
    ahoy_type_name: stageToEntity,
    ahoy_display_name: pluralName.trim(),
    ahoy_display_name_reverse: 'Pipeline Stage',
    ahoy_source_entity_type_name: stageTypeName,
    ahoy_target_entity_type_name: entityTypeName,
    ahoy_cardinality: 'one_to_many',
    ahoy_reverse_type_name: entityToStage,
  });
  step3Builder.objectCreated(crypto.randomUUID(), 'ahoy_association_type', {
    ahoy_type_name: pipelineToStage,
    ahoy_display_name: 'Stages',
    ahoy_display_name_reverse: 'Pipeline',
    ahoy_source_entity_type_name: pipelineTypeName,
    ahoy_target_entity_type_name: stageTypeName,
    ahoy_cardinality: 'one_to_many',
    ahoy_reverse_type_name: stageToPipeline,
  });
  step3Builder.objectCreated(crypto.randomUUID(), 'ahoy_association_type', {
    ahoy_type_name: stageToPipeline,
    ahoy_display_name: 'Pipeline',
    ahoy_display_name_reverse: 'Stages',
    ahoy_source_entity_type_name: stageTypeName,
    ahoy_target_entity_type_name: pipelineTypeName,
    ahoy_cardinality: 'many_to_one',
    ahoy_reverse_type_name: pipelineToStage,
  });
  // Forward transitive: entity → pipeline (via stage)
  step3Builder.objectCreated(crypto.randomUUID(), 'ahoy_association_type', {
    ahoy_type_name: entityToPipeline,
    ahoy_display_name: 'Pipeline',
    ahoy_display_name_reverse: pluralName.trim(),
    ahoy_source_entity_type_name: entityTypeName,
    ahoy_target_entity_type_name: pipelineTypeName,
    ahoy_cardinality: 'many_to_one',
    ahoy_value_write_access: 'internal',
    ahoy_value_read_access: 'internal',
    ahoy_reverse_type_name: pipelineToEntity,
    ahoy_transitive_via: [entityToStage, stageToPipeline],
  });
  // Reverse transitive: pipeline → entity (via stage)
  step3Builder.objectCreated(crypto.randomUUID(), 'ahoy_association_type', {
    ahoy_type_name: pipelineToEntity,
    ahoy_display_name: pluralName.trim(),
    ahoy_display_name_reverse: 'Pipeline',
    ahoy_source_entity_type_name: pipelineTypeName,
    ahoy_target_entity_type_name: entityTypeName,
    ahoy_cardinality: 'one_to_many',
    ahoy_value_write_access: 'internal',
    ahoy_value_read_access: 'internal',
    ahoy_reverse_type_name: entityToPipeline,
    ahoy_transitive_via: [pipelineToStage, stageToEntity],
  });
  steps.push({
    label: 'Setting up pipeline associations',
    events: step3Builder.build(),
  });

  // Batch 4: Create default pipeline and stages
  const step4Builder = createEventBuilder({ sourceId });
  const pipelineId = crypto.randomUUID();
  step4Builder.objectCreated(pipelineId, pipelineTypeName, {
    ahoy_name: pipelineName.trim(),
  });
  stages.forEach(stage => {
    const stageId = crypto.randomUUID();
    step4Builder.objectCreated(stageId, stageTypeName, {
      ahoy_name: stage.name.trim(),
      ahoy_order: String(stage.order),
    });
    step4Builder.associationAdded(
      stageId,
      stageTypeName,
      `${stageTypeName}_to_${pipelineTypeName}`,
      pipelineId
    );
    step4Builder.associationAdded(
      pipelineId,
      pipelineTypeName,
      `${pipelineTypeName}_to_${stageTypeName}`,
      stageId
    );
  });
  steps.push({
    label: 'Creating default pipeline and stages',
    events: step4Builder.build(),
  });

  // Batch 5 (optional): Custom properties on the main entity type
  const propStep = buildPropertyStep(sourceId, entityTypeName, properties);
  if (propStep) {
    steps.push(propStep);
  }

  // Batch 6 (optional): User-defined associations on the main entity type
  const assocStep = buildAssociationStep(
    sourceId,
    entityTypeName,
    associations
  );
  if (assocStep) {
    steps.push(assocStep);
  }

  // Batch 7 (optional): Enable notes on the main entity type
  const notesStep = buildNotesStep(
    sourceId,
    entityTypeName,
    notesEnabled,
    pluralName,
    displayName
  );
  if (notesStep) {
    steps.push(notesStep);
  }

  return steps;
}
